import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/Button';
import { FormTextarea } from '@/components/FormTextarea';
import { useToast } from '@/hooks/useToast';
import type { ManagerSuggestion } from '@/features/Vacations/hooks/data/useManagerSuggestions';
import { ClientEmailsInput } from '@/features/Vacations/components/VacationRequestForm/components/ClientEmailsInput';
import { ManagerField } from '../ManagerField';
import { useSubmitDayOff } from '../../hooks/data/useDpRequestMutations';
import { dayOffFormSchema } from '../../models/dayOff.schemas';
import { DP_REQUESTS_PATH } from '../../routes';

interface DayOffRequestFormProps {
    suggestions: ManagerSuggestion[];
    disabled: boolean;
}

type DayOffErrors = Partial<Record<'managerEmail' | 'clientEmails' | 'reason', string>>;

export function DayOffRequestForm({ suggestions, disabled }: DayOffRequestFormProps) {
    const navigate = useNavigate();
    const toast = useToast();
    const submit = useSubmitDayOff();
    const [managerEmail, setManagerEmail] = useState('');
    const [clientEmails, setClientEmails] = useState<string[]>([]);
    const [noClient, setNoClient] = useState(false);
    const [reason, setReason] = useState('');
    const [errors, setErrors] = useState<DayOffErrors>({});

    async function handleSubmit() {
        const parsed = dayOffFormSchema.safeParse({ managerEmail, reason, noClient, clientEmails: noClient ? [] : clientEmails });
        if (!parsed.success) {
            const next: DayOffErrors = {};
            parsed.error.issues.forEach(issue => {
                const field = issue.path[0] as keyof DayOffErrors;
                if (!next[field]) next[field] = issue.message;
            });
            setErrors(next);
            toast.error('Revise o pedido', Object.values(next)[0]);
            return;
        }

        setErrors({});
        try {
            await submit.mutateAsync({
                managerEmail: parsed.data.managerEmail,
                reason: parsed.data.reason || undefined,
                clientEmails: parsed.data.clientEmails,
                noClient: parsed.data.noClient,
            });
            navigate(DP_REQUESTS_PATH);
        } catch {
            return;
        }
    }

    const busy = submit.isPending || disabled;

    return (
        <div className="space-y-5 rounded-2xl border border-raro-gray-200 bg-white p-5 shadow-sm sm:p-6">
            <ManagerField
                label="Gestor Raro que aprova"
                value={managerEmail}
                onChange={setManagerEmail}
                suggestions={suggestions}
                error={errors.managerEmail}
                disabled={busy}
            />

            <div className="space-y-2">
                <p className="text-sm font-semibold text-raro-gray-800">
                    E-mail do(s) gestor(es) do cliente
                    {!noClient && <span className="ml-1 text-rose-600">*</span>}
                </p>
                <p className="text-xs text-raro-gray-500">Eles recebem um link para aprovar ou recusar o day off, sem precisar de login.</p>
                {!noClient && (
                    <ClientEmailsInput
                        emails={clientEmails}
                        onAdd={email => setClientEmails(current => [...current, email])}
                        onRemove={index => setClientEmails(current => current.filter((_, position) => position !== index))}
                        disabled={busy}
                        error={errors.clientEmails}
                    />
                )}
                <label className="flex cursor-pointer items-center gap-2 text-sm text-raro-gray-700">
                    <input
                        type="checkbox"
                        checked={noClient}
                        onChange={event => setNoClient(event.target.checked)}
                        disabled={busy}
                        className="h-4 w-4 rounded border-raro-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    Atuo em projeto interno, sem cliente
                </label>
                {noClient && errors.clientEmails && <p className="text-xs text-rose-600">{errors.clientEmails}</p>}
            </div>

            <FormTextarea
                label="Observação (opcional)"
                name="reason"
                value={reason}
                onChange={event => setReason(event.target.value)}
                maxLength={500}
                showCharCount
                rows={2}
                disabled={busy}
            />

            <div className="flex justify-end gap-3">
                <Button variant="ghost" onClick={() => navigate(DP_REQUESTS_PATH)} disabled={busy}>
                    Cancelar
                </Button>
                <Button onClick={() => void handleSubmit()} loading={submit.isPending} disabled={busy || !managerEmail}>
                    Enviar para aprovação
                </Button>
            </div>
        </div>
    );
}
